import { useAuthStore } from "../auth/auth.store";

const API_BASE = "/api";

interface RequestOpts extends RequestInit {
  json?: unknown;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string; user: import("@gryzonie/shared").PublicUserDto };
      useAuthStore.getState().setSession(data.accessToken, data.user);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { json, skipAuth, skipRefresh, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    credentials: "include",
    headers: {
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : (rest.body as BodyInit | null | undefined),
  };

  if (!skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      (init.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
  }

  let res = await fetch(`${API_BASE}${path}`, init);
  if (res.status === 401 && !skipRefresh && !skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        (init.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
      }
      res = await fetch(`${API_BASE}${path}`, init);
    } else {
      useAuthStore.getState().clear();
    }
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.message) {
        message = Array.isArray(body.message) ? body.message.join(", ") : String(body.message);
      }
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export const refreshSession = tryRefresh;
